/**
 * Phase 16 — Hook to read a ticket's current business state + history.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TicketBusinessState, TransitionKind } from '@/lib/tickets/businessStateMachine';

export interface BusinessStateRow {
  ticket_id: string;
  shop_id: string;
  current_state: TicketBusinessState;
  previous_state: TicketBusinessState | null;
  entered_at: string;
  entered_by: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
}

export interface BusinessStateHistoryRow {
  id: string;
  ticket_id: string;
  shop_id: string;
  from_state: TicketBusinessState | null;
  to_state: TicketBusinessState;
  transition_kind: TransitionKind;
  actor_id: string | null;
  actor_role: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export function useTicketBusinessState(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ticket-business-state', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<BusinessStateRow | null> => {
      const { data, error } = await (supabase as any)
        .from('ticket_business_state')
        .select('*')
        .eq('ticket_id', ticketId)
        .maybeSingle();
      if (error) throw error;
      return data as BusinessStateRow | null;
    },
  });
}

export function useTicketBusinessStateHistory(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ticket-business-state-history', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<BusinessStateHistoryRow[]> => {
      const { data, error } = await (supabase as any)
        .from('ticket_business_state_history')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as BusinessStateHistoryRow[];
    },
  });
}

export function useReadyToInvoiceEligibility(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ready-to-invoice-eligibility', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<{ ready: boolean; blockers: string[] }> => {
      const { data, error } = await (supabase as any).rpc('check_ready_to_invoice_eligibility', {
        p_ticket_id: ticketId,
      });
      if (error) throw error;
      return (data ?? { ready: false, blockers: [] }) as { ready: boolean; blockers: string[] };
    },
  });
}
